/**
 * MoveWebABTestToFolderDialog Component
 *
 * Dialog for moving a Web A/B test to a different folder.
 * Shows list of available folders with "Root (no folder)" option.
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import {
  Close as CloseIcon,
  Folder as FolderIcon,
  FolderOpen as RootIcon,
} from '@mui/icons-material';
import type { WebABTestFolder } from '@modules/firebase-core/converters/webABTestFolderConverter';

export interface MoveWebABTestToFolderDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback when folder is selected */
  onMove: (folderId: string | null) => Promise<void>;
  /** Available folders */
  folders: WebABTestFolder[];
  /** Current folder ID to exclude from options */
  currentFolderId?: string | null;
  /** Item name being moved (for display) */
  itemName?: string;
}

/**
 * MoveWebABTestToFolderDialog Component
 */
export function MoveWebABTestToFolderDialog({
  open,
  onClose,
  onMove,
  folders,
  currentFolderId,
  itemName,
}: MoveWebABTestToFolderDialogProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Filter out current folder from options
  const availableFolders = folders.filter(f => f.id !== currentFolderId);

  const handleSelect = useCallback((folderId: string | null) => {
    setSelectedFolderId(folderId);
  }, []);

  const handleMove = useCallback(async () => {
    setLoading(true);
    try {
      await onMove(selectedFolderId);
      onClose();
    } catch (err) {
      console.error('Move failed:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedFolderId, onMove, onClose]);

  const handleClose = useCallback(() => {
    setSelectedFolderId(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      data-testid="webabt-move-dialog"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            backdropFilter: 'blur(20px)',
          }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'grid',
          gridTemplateColumns: '40px 1fr 40px',
          alignItems: 'center',
          gap: 1,
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
          <IconButton
            onClick={handleClose}
            disabled={loading}
            size="small"
            aria-label="close dialog"
            data-testid="webabt-move-close-btn"
            sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          component="span"
          sx={{
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          Move to Folder
        </Box>
        <Box />
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {itemName && (
          <Box sx={{ px: 3, py: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Moving: <strong>{itemName}</strong>
            </Typography>
          </Box>
        )}
        <List sx={{ pt: 0 }}>
          {/* Root option */}
          <ListItemButton
            selected={selectedFolderId === null}
            onClick={() => handleSelect(null)}
            data-testid="webabt-move-root"
          >
            <ListItemIcon>
              <RootIcon color={selectedFolderId === null ? 'primary' : 'inherit'} />
            </ListItemIcon>
            <ListItemText primary="Root (no folder)" />
          </ListItemButton>

          {/* Available folders */}
          {availableFolders.map((folder) => (
            <ListItemButton
              key={folder.id}
              selected={selectedFolderId === folder.id}
              onClick={() => handleSelect(folder.id)}
              data-testid={`webabt-move-folder-${folder.id}`}
            >
              <ListItemIcon>
                <FolderIcon color={selectedFolderId === folder.id ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary={folder.name} />
            </ListItemButton>
          ))}

          {availableFolders.length === 0 && (
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No other folders available
              </Typography>
            </Box>
          )}
        </List>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleMove}
          variant="contained"
          disabled={loading}
          data-testid="webabt-move-btn"
        >
          {loading ? <CircularProgress size={18} /> : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MoveWebABTestToFolderDialog;
